import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/common/ui/form'
import { Input } from '@/common/ui/input'
import { ArrowUpFromLine } from 'lucide-react'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import { BankSelector } from '@/modules/bank/components/BankSelector'
import useMutationCreateBankWithdrawalTransaction from '@/modules/bank/api/hooks/useMutationCreateBankWithdrawalTransaction'
import { RequiredIndicator } from '@/common/ui/form/required-indicator'
import type { PaymentChannelTab } from '@/modules/bank/type'
import { CreateOperatorWithdrawTransactionDtoChannel } from '@/schema'

const formSchema = z.object({
	amount: z.coerce.number().positive('จำนวนเงินต้องมากกว่า 0'),
	bankProviderName: z.string().min(1, 'กรุณาเลือกธนาคาร'),
	accountNumber: z.string().min(1, 'กรุณากรอกเลขบัญชี'),
	accountName: z.string().min(1, 'กรุณากรอกชื่อบัญชี'),
	phoneNumber: z.string().min(1, 'กรุณากรอกเบอร์โทรศัพท์'),
})

type FormValues = z.infer<typeof formSchema>

interface CreateWithdrawalDialogProps {
	paymentGatewayId: string
	displayName: string
	/** Current tab - mapped to WITHDRAW_BANK or WITHDRAW_TRUE_MONEY when sending to server */
	channel?: PaymentChannelTab
}

export default function CreateWithdrawalDialog({
	paymentGatewayId,
	displayName,
	channel,
}: CreateWithdrawalDialogProps) {
	const [open, setOpen] = useState(false)
	const { mutateAsync, isPending } =
		useMutationCreateBankWithdrawalTransaction()

	const form = useForm<FormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			amount: 0,
			bankProviderName: '',
			accountNumber: '',
			accountName: '',
			phoneNumber: '',
		},
	})

	const handleSubmit = async (values: FormValues) => {
		try {
			await mutateAsync({
				payload: {
					paymentGatewayId,
					amount: values.amount,
					bankProviderName: values.bankProviderName,
					accountNumber: values.accountNumber,
					accountName: values.accountName,
					phoneNumber: values.phoneNumber,
					...(channel !== undefined && {
						channel:
							channel === 'TRUE_MONEY'
								? ('WITHDRAW_TRUE_MONEY' as CreateOperatorWithdrawTransactionDtoChannel)
								: ('WITHDRAW_BANK' as CreateOperatorWithdrawTransactionDtoChannel),
					}),
				},
			})
			toast.success('เพิ่มรายการถอนเงินสำเร็จ')
			setOpen(false)
			form.reset()
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	return (
		<>
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							variant="outline"
							size="icon"
							onClick={() => setOpen(true)}
							className="h-8 w-8"
						>
							<ArrowUpFromLine className="h-4 w-4" />
						</Button>
					</TooltipTrigger>
					<TooltipContent>
						<p>ถอนเงิน</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-[500px]">
					<DialogHeader>
						<DialogTitle>เพิ่มรายการถอนเงิน - {displayName}</DialogTitle>
					</DialogHeader>
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(handleSubmit)}
							className="space-y-6"
						>
							<FormField
								control={form.control}
								name="amount"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> จำนวนเงิน
										</FormLabel>
										<FormControl>
											<Input
												type="number"
												placeholder="กรอกจำนวนเงิน"
												{...field}
												onKeyDown={(e) => {
													if (['e', 'E', '+', '-'].includes(e.key)) {
														e.preventDefault()
													}
												}}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="phoneNumber"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> เบอร์โทรศัพท์
										</FormLabel>
										<FormControl>
											<Input
												type="tel"
												placeholder="กรอกเบอร์โทรศัพท์"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<BankSelector
								control={form.control}
								name="bankProviderName"
								label="ธนาคาร"
								required={true}
							/>

							<FormField
								control={form.control}
								name="accountNumber"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> เลขบัญชี
										</FormLabel>
										<FormControl>
											<Input placeholder="กรอกเลขบัญชี" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="accountName"
								render={({ field }) => (
									<FormItem>
										<FormLabel>
											<RequiredIndicator /> ชื่อบัญชี
										</FormLabel>
										<FormControl>
											<Input placeholder="กรอกชื่อบัญชี" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							<div className="flex justify-end gap-2">
								<Button
									type="button"
									variant="outline"
									onClick={() => {
										setOpen(false)
										form.reset()
									}}
									disabled={isPending}
								>
									ยกเลิก
								</Button>
								<Button type="submit" disabled={isPending}>
									{isPending ? 'กำลังบันทึก...' : 'บันทึก'}
								</Button>
							</div>
						</form>
					</Form>
				</DialogContent>
			</Dialog>
		</>
	)
}
